/**
 * 🔗 VENDOR MAGIC BID PAGE
 * Unauthenticated vendor experience via magic links.
 * Part of the "Demand-First Vendor Outreach" system.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  Spin,
  Alert,
  Typography,
  Input,
  Button,
  Form,
  InputNumber,
  message,
  Divider,
  Tag,
  Space,
} from 'antd';
import {
  DollarOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContentCard = styled(Card)`
  max-width: 800px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const VendorHeader = styled.div`
  text-align: center;
  padding: 1.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 1.5rem;
`;

const JobSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const MarketAnchor = styled.div`
  background: linear-gradient(135deg, #ff6b6b, #ffa500);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 1.5rem;
  
  .anchor-amount {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
  }
  
  .anchor-label {
    opacity: 0.9;
    font-size: 0.9rem;
  }
`;

const ChatSection = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  max-height: 300px;
  overflow-y: auto;
`;

const ChatMessage = styled.div`
  margin-bottom: 1rem;
  
  &.vendor-message {
    text-align: right;
    
    .message-bubble {
      background: #1890ff;
      color: white;
      padding: 0.5rem 1rem;
      border-radius: 18px 18px 4px 18px;
      display: inline-block;
      max-width: 80%;
    }
  }
  
  &.ai-message {
    text-align: left;
    
    .message-bubble {
      background: #f0f0f0;
      color: #333;
      padding: 0.5rem 1rem;
      border-radius: 18px 18px 18px 4px;
      display: inline-block;
      max-width: 80%;
    }
  }
`;

const BidSection = styled.div`
  background: ${props => props.isBackupMode ? '#fff7e6' : '#e6f7ff'};
  border: 1px solid ${props => props.isBackupMode ? '#ffd591' : '#91d5ff'};
  border-radius: 8px;
  padding: 1.5rem;
`;

const BackupStatusCard = styled.div`
  background: linear-gradient(135deg, #fa8c16, #fadb14);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #fa541c;
  
  .backup-title {
    font-size: 1.1rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .backup-description {
    font-size: 0.95rem;
    line-height: 1.4;
    margin: 0;
  }
`;

const LinkStatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => {
    switch (props.status) {
      case 'ACTIVE': return '#f6ffed';
      case 'STANDBY': return '#fff7e6';
      case 'CLOSED': return '#fff1f0';
      default: return '#fafafa';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'ACTIVE': return '#b7eb8f';
      case 'STANDBY': return '#ffd591';
      case 'CLOSED': return '#ffccc7';
      default: return '#d9d9d9';
    }
  }};
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  
  .status-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .status-badge {
    font-size: 0.8rem;
    font-weight: bold;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
    background: ${props => {
      switch (props.status) {
        case 'ACTIVE': return '#52c41a';
        case 'STANDBY': return '#fa8c16';
        case 'CLOSED': return '#ff4d4f';
        default: return '#8c8c8c';
      }
    }};
    color: white;
  }
  
  .time-remaining {
    font-size: 0.85rem;
    color: #666;
  }
`;

const VendorMagicBidPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [bidSubmitted, setBidSubmitted] = useState(false);
  const [form] = Form.useForm();

  const fetchMagicLinkContext = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/public/magic-bid/${token}`);
      const result = await response.json();

      if (result.success) {
        setData(result);
        
        // Add welcome message from Leo
        setChatMessages([{
          type: 'ai',
          message: `Welcome, ${result.vendor.businessName}! I'm Clumse AI, your negotiation assistant. I've reviewed the ${result.job.category} job details. Let's discuss your bid for this project.`,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        setError(result.error || 'Invalid or expired magic link');
      }
    } catch (err) {
      console.error('Error fetching magic link context:', err);
      setError('Failed to load job details. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch magic link context on page load
  useEffect(() => {
    fetchMagicLinkContext();
  }, [fetchMagicLinkContext]);

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    try {
      setSendingMessage(true);
      
      // Add vendor message to chat
      const vendorMessage = {
        type: 'vendor',
        message: chatInput,
        timestamp: new Date().toISOString(),
      };
      setChatMessages(prev => [...prev, vendorMessage]);
      
      const messageToSend = chatInput;
      setChatInput('');

      // Send to API
      const response = await fetch(`/api/public/magic-bid/${token}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: messageToSend }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Add AI response to chat
        const aiMessage = {
          type: 'ai',
          message: result.response.message,
          timestamp: new Date().toISOString(),
        };
        setChatMessages(prev => [...prev, aiMessage]);
      } else {
        message.error('Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      message.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const submitBid = async (values) => {
    try {
      setSubmittingBid(true);
      
      const response = await fetch(`/api/public/magic-bid/${token}/bid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const result = await response.json();
      
      if (result.success) {
        setBidSubmitted(true);
        message.success('Bid submitted successfully!');
      } else {
        message.error(result.error || 'Failed to submit bid');
      }
    } catch (err) {
      console.error('Error submitting bid:', err);
      message.error('Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const formatPhotos = (photos) => {
    if (!photos || photos.length === 0) return null;
    
    return (
      <div style={{ marginTop: '1rem' }}>
        <Text strong>Job Photos:</Text>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          {photos.map((photo, index) => (
            <img
              key={index}
              src={photo}
              alt={`Job photo ${index + 1}`}
              style={{
                width: '150px',
                height: '150px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <Spin size="large" />
          <div style={{ color: 'white', marginTop: '1rem', fontSize: '1.2rem' }}>
            Loading job details...
          </div>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ContentCard>
          <Alert
            type="error"
            showIcon
            message="Invalid Magic Link"
            description={error}
            action={
              <Button 
                type="primary" 
                onClick={() => navigate('/')}
              >
                Go to Home Page
              </Button>
            }
          />
        </ContentCard>
      </PageContainer>
    );
  }

  if (bidSubmitted) {
    const isBackupSubmission = data.linkStatus?.isBackupMode;
    return (
      <PageContainer>
        <ContentCard>
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            {isBackupSubmission ? (
              <TeamOutlined style={{ fontSize: '4rem', color: '#fa8c16', marginBottom: '1rem' }} />
            ) : (
              <CheckCircleOutlined style={{ fontSize: '4rem', color: '#52c41a', marginBottom: '1rem' }} />
            )}
            <Title level={2}>
              {isBackupSubmission ? 'Backup Bid Registered!' : 'Bid Submitted Successfully!'}
            </Title>
            <Paragraph>
              Thank you, {data.vendor.businessName}. 
              {isBackupSubmission ? (
                <> Your backup bid has been registered for the {data.job.category} job.
                You'll be prioritized if the primary provider can't fulfill the schedule.</>
              ) : (
                <> Your bid has been submitted for the {data.job.category} job.
                The property owner will review all bids and contact you if your proposal is selected.</>
              )}
            </Paragraph>
            <Button type="primary" onClick={() => navigate('/')}>
              Return to Clumse Home
            </Button>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentCard>
        <VendorHeader>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            {data.linkStatus?.isBackupMode ? 'Submit a Backup Bid' : 'Bid on this Job'}
          </Title>
          <Text type="secondary">Magic Link Access for {data.vendor.businessName}</Text>
        </VendorHeader>

        {/* 🦈 Link Status Indicator */}
        <LinkStatusIndicator status={data.linkStatus?.status}>
          <div className="status-info">
            <div className="status-badge">{data.linkStatus?.status || 'ACTIVE'}</div>
            <span>
              {data.linkStatus?.status === 'ACTIVE' && 'Open for bids'}
              {data.linkStatus?.status === 'STANDBY' && 'Accepting backup bids'}
              {data.linkStatus?.status === 'CLOSED' && 'Bidding closed'}
            </span>
          </div>
          {data.linkStatus?.hoursRemaining > 0 && (
            <div className="time-remaining">
              <ClockCircleOutlined /> {data.linkStatus.hoursRemaining} hours remaining
            </div>
          )}
        </LinkStatusIndicator>

        {/* 🦈 Backup Mode Status Card */}
        {data.linkStatus?.isBackupMode && (
          <BackupStatusCard>
            <div className="backup-title">
              <TeamOutlined />
              Backup Provider Opportunity
            </div>
            <p className="backup-description">
              A service provider has been shortlisted. We are accepting backup bids to ensure the homeowner's job is completed. 
              Backup bids are prioritized if the primary provider is unable to fulfill the schedule.
            </p>
          </BackupStatusCard>
        )}

        {/* Market Anchor */}
        <MarketAnchor>
          <div className="anchor-amount">
            <DollarOutlined /> {data.bidding.marketAnchor?.toLocaleString() || 'TBD'}
          </div>
          <div className="anchor-label">Market Anchor • Competitive Pricing Opportunity</div>
        </MarketAnchor>

        {/* Job Details */}
        <JobSection>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Space>
                <ToolOutlined style={{ color: '#1890ff' }} />
                <Title level={4} style={{ margin: 0 }}>{data.ticket.title}</Title>
                <Tag color="blue">{data.ticket.category}</Tag>
                <Tag color={data.ticket.priority === 'high' ? 'red' : 'default'}>
                  {data.ticket.priority} priority
                </Tag>
              </Space>
            </div>
            
            <div>
              <Space>
                <EnvironmentOutlined style={{ color: '#52c41a' }} />
                <Text>Location: {data.bidding.city} (Full address shared after bid acceptance)</Text>
              </Space>
            </div>

            <Paragraph>{data.ticket.description}</Paragraph>
            
            {formatPhotos(data.ticket.photos)}
          </Space>
        </JobSection>

        {/* Vendor negotiation chat */}
        <div>
          <Title level={4}>
            <MessageOutlined /> Discuss with Clumse AI
          </Title>
          
          <ChatSection>
            {chatMessages.map((msg, index) => (
              <ChatMessage key={index} className={`${msg.type}-message`}>
                <div className="message-bubble">
                  {msg.message}
                </div>
              </ChatMessage>
            ))}
          </ChatSection>

          <Input.Group compact>
            <Input
              style={{ width: 'calc(100% - 100px)' }}
              placeholder="Ask Clumse AI about the job or discuss your bid..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onPressEnter={sendChatMessage}
              disabled={sendingMessage}
            />
            <Button 
              type="primary"
              icon={<SendOutlined />}
              onClick={sendChatMessage}
              loading={sendingMessage}
              style={{ width: '100px' }}
            >
              Send
            </Button>
          </Input.Group>
        </div>

        <Divider />

        {/* Bid Submission */}
        {data.canBid && (
          <BidSection isBackupMode={data.linkStatus?.isBackupMode}>
            <Title level={4}>
              {data.linkStatus?.isBackupMode ? 'Register as Backup Provider' : 'Submit Your Bid'}
            </Title>
            <Form
              form={form}
              onFinish={submitBid}
              layout="vertical"
            >
              <Form.Item
                name="bidAmount"
                label={data.linkStatus?.isBackupMode ? "Backup Bid Amount ($)" : "Bid Amount ($)"}
                rules={[
                  { required: true, message: 'Please enter your bid amount' },
                  { type: 'number', min: 1, message: 'Bid must be at least $1' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  placeholder={
                    data.linkStatus?.isBackupMode 
                      ? "Enter your backup bid amount" 
                      : "Enter your bid amount"
                  }
                />
              </Form.Item>

              <Form.Item
                name="estimatedDuration"
                label="Estimated Duration"
              >
                <Input placeholder="e.g. 2-3 hours, Same day, 1 week" />
              </Form.Item>

              <Form.Item
                name="notes"
                label="Additional Notes"
              >
                <Input.TextArea
                  placeholder={
                    data.linkStatus?.isBackupMode
                      ? "Details about your availability as backup provider, materials, or approach..."
                      : "Any additional details about your bid, materials, or approach..."
                  }
                  rows={4}
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  size="large"
                  loading={submittingBid}
                  block
                  style={{
                    background: data.linkStatus?.isBackupMode ? '#fa8c16' : '#1890ff',
                    borderColor: data.linkStatus?.isBackupMode ? '#fa8c16' : '#1890ff',
                  }}
                >
                  {data.linkStatus?.isBackupMode ? 'Register as Backup Provider' : 'Submit Bid'}
                </Button>
              </Form.Item>
            </Form>
          </BidSection>
        )}

        {!data.canBid && (
          <Alert
            type={data.linkStatus?.status === 'CLOSED' ? 'error' : 'warning'}
            message={
              data.linkStatus?.status === 'CLOSED' 
                ? 'Link Expired' 
                : 'Bidding Unavailable'
            }
            description={
              data.linkStatus?.status === 'CLOSED'
                ? 'This magic link has expired for PII protection (72 hours). The bidding window has closed.'
                : 'Bidding is currently unavailable for this job.'
            }
            showIcon
          />
        )}
      </ContentCard>
    </PageContainer>
  );
};

export default VendorMagicBidPage;